import type { 
  SpatialContext, 
  SpatialScope, 
  ReportMode 
} from './domain';

export interface ReportIntentClassification {
  isReportIntent: boolean;
  requestedMode?: ReportMode;
  requestedLocation?: string;
  confidence: number;
}

export interface ReportTargetResolution {
  resolvedLocation: string | null;
  spatialContext: SpatialContext | null;
  isOverridden: boolean;
  needsClarification: boolean;
  clarificationPrompt?: string;
}

export interface SessionCoordinator {
  createRequest: (location: string) => { requestId: string; location: string };
  isLatest: (requestId: string) => boolean;
  shouldApplyResult: (requestId: string) => boolean;
  tryDispatch: (dispatchId: string) => boolean;
  releaseDispatch?: (dispatchId: string) => void;
}

export class ReportIntentClassifier {
  /**
   * Comprehensive sovereign nation set (~200 entries).
   * Used by resolveScope to distinguish NATIONAL from METRO/LOCAL.
   * Includes common aliases (uk, usa, etc.).
   */
  private static KNOWN_COUNTRIES = new Set([
    // A
    'afghanistan', 'albania', 'algeria', 'andorra', 'angola', 'antigua and barbuda',
    'argentina', 'armenia', 'australia', 'austria', 'azerbaijan',
    // B
    'bahamas', 'bahrain', 'bangladesh', 'barbados', 'belarus', 'belgium', 'belize',
    'benin', 'bhutan', 'bolivia', 'bosnia and herzegovina', 'botswana', 'brazil',
    'brunei', 'bulgaria', 'burkina faso', 'burundi',
    // C
    'cabo verde', 'cambodia', 'cameroon', 'canada', 'central african republic', 'chad',
    'chile', 'china', 'colombia', 'comoros', 'congo', 'costa rica', 'croatia', 'cuba',
    'cyprus', 'czech republic', 'czechia',
    // D
    'democratic republic of the congo', 'denmark', 'djibouti', 'dominica',
    'dominican republic',
    // E
    'ecuador', 'egypt', 'el salvador', 'equatorial guinea', 'eritrea', 'estonia',
    'eswatini', 'ethiopia',
    // F
    'fiji', 'finland', 'france',
    // G
    'gabon', 'gambia', 'georgia', 'germany', 'ghana', 'greece', 'grenada', 'guatemala',
    'guinea', 'guinea-bissau', 'guyana',
    // H
    'haiti', 'honduras', 'hungary',
    // I
    'iceland', 'india', 'indonesia', 'iran', 'iraq', 'ireland', 'israel', 'italy',
    'ivory coast',
    // J
    'jamaica', 'japan', 'jordan',
    // K
    'kazakhstan', 'kenya', 'kiribati', 'kosovo', 'kuwait', 'kyrgyzstan',
    // L
    'laos', 'latvia', 'lebanon', 'lesotho', 'liberia', 'libya', 'liechtenstein',
    'lithuania', 'luxembourg',
    // M
    'madagascar', 'malawi', 'malaysia', 'maldives', 'mali', 'malta', 'marshall islands',
    'mauritania', 'mauritius', 'mexico', 'micronesia', 'moldova', 'monaco', 'mongolia',
    'montenegro', 'morocco', 'mozambique', 'myanmar',
    // N
    'namibia', 'nauru', 'nepal', 'netherlands', 'new zealand', 'nicaragua', 'niger',
    'nigeria', 'north korea', 'north macedonia', 'norway',
    // O
    'oman',
    // P
    'pakistan', 'palau', 'palestine', 'panama', 'papua new guinea', 'paraguay', 'peru',
    'philippines', 'poland', 'portugal',
    // Q
    'qatar',
    // R
    'romania', 'russia', 'rwanda',
    // S
    'saint kitts and nevis', 'saint lucia', 'saint vincent and the grenadines', 'samoa',
    'san marino', 'sao tome and principe', 'saudi arabia', 'senegal', 'serbia',
    'seychelles', 'sierra leone', 'singapore', 'slovakia', 'slovenia', 'solomon islands',
    'somalia', 'south africa', 'south korea', 'south sudan', 'spain', 'sri lanka',
    'sudan', 'suriname', 'sweden', 'switzerland', 'syria',
    // T
    'taiwan', 'tajikistan', 'tanzania', 'thailand', 'timor-leste', 'togo', 'tonga',
    'trinidad and tobago', 'tunisia', 'turkey', 'turkmenistan', 'tuvalu',
    // U
    'uganda', 'ukraine', 'united arab emirates', 'united kingdom', 'united states',
    'uruguay', 'uzbekistan',
    // V
    'vanuatu', 'vatican city', 'venezuela', 'vietnam',
    // Y
    'yemen',
    // Z
    'zambia', 'zimbabwe',
    // Common aliases
    'uk', 'usa', 'uae', 'drc', 'car'
  ]);

  /**
   * ISO 3166-1 alpha-2 country codes for structured geocoder signals.
   */
  private static KNOWN_COUNTRY_CODES = new Set([
    'AF','AL','DZ','AD','AO','AG','AR','AM','AU','AT','AZ','BS','BH','BD','BB','BY',
    'BE','BZ','BJ','BT','BO','BA','BW','BR','BN','BG','BF','BI','CV','KH','CM','CA',
    'CF','TD','CL','CN','CO','KM','CG','CD','CR','HR','CU','CY','CZ','DK','DJ','DM',
    'DO','EC','EG','SV','GQ','ER','EE','SZ','ET','FJ','FI','FR','GA','GM','GE','DE',
    'GH','GR','GD','GT','GN','GW','GY','HT','HN','HU','IS','IN','ID','IR','IQ','IE',
    'IL','IT','CI','JM','JP','JO','KZ','KE','KI','XK','KW','KG','LA','LV','LB','LS',
    'LR','LY','LI','LT','LU','MG','MW','MY','MV','ML','MT','MH','MR','MU','MX','FM',
    'MD','MC','MN','ME','MA','MZ','MM','NA','NR','NP','NL','NZ','NI','NE','NG','KP',
    'MK','NO','OM','PK','PW','PS','PA','PG','PY','PE','PH','PL','PT','QA','RO','RU',
    'RW','KN','LC','VC','WS','SM','ST','SA','SN','RS','SC','SL','SG','SK','SI','SB',
    'SO','ZA','KR','SS','ES','LK','SD','SR','SE','CH','SY','TW','TJ','TZ','TH','TL',
    'TG','TO','TT','TN','TR','TM','TV','UG','UA','AE','GB','US','UY','UZ','VU','VA',
    'VE','VN','YE','ZM','ZW'
  ]);

  /**
   * Classifies whether a given user utterance is an explicit report request.
   * Enforces cost control: Navigation, generic questions, and weather queries
   * NEVER trigger full canonical report generation.
   */
  public classifyIntent(utterance: string): ReportIntentClassification {
    if (!utterance || typeof utterance !== 'string') {
      return { isReportIntent: false, confidence: 0 };
    }

    const text = utterance.trim().toLowerCase();

    // 🛡️ SYSTEM ANNOUNCEMENT & ACOUSTIC ECHO GUARD:
    // Never classify assistant completion announcements or system status phrases as user report requests!
    if (
      text.startsWith('i have compiled') ||
      text.startsWith("i've compiled") ||
      text.startsWith('i compiled') ||
      text.startsWith('the briefing is ready') ||
      text.startsWith('briefing is ready') ||
      text.startsWith('the report is ready') ||
      text.startsWith('report is ready') ||
      text.startsWith('here is the report') ||
      text.startsWith('here is your report') ||
      text.includes('the briefing is ready in spatial command') ||
      text.includes('briefing is ready in spatial command')
    ) {
      return { isReportIntent: false, confidence: 0.99 };
    }

    // 🛡️ COST CONTROL: Exclude navigation, generic questions, and weather checks
    if (
      text.startsWith('fly to') ||
      text.startsWith('take me to') ||
      text.startsWith('go to') ||
      text.startsWith('navigate to') ||
      text.startsWith('show me the map')
    ) {
      return { isReportIntent: false, confidence: 0.95 };
    }

    if (
      text.includes("what's happening") ||
      text.includes("what is happening") ||
      text.includes("what's going on") ||
      text.includes("tell me about") ||
      text.includes("what's nearby") ||
      text.includes("any news") ||
      text.includes("what's the weather") ||
      text.includes("what is the weather")
    ) {
      // Unless they explicitly ask for a "report", these are conversational
      if (!text.includes('report')) {
        return { isReportIntent: false, confidence: 0.9 };
      }
    }

    // Explicit report patterns:
    // "write a report", "write a history report", "create a travel report", "technical report", etc.
    const reportPattern = /\b(?:write|generate|compile|create|prepare|give me|build)\s+(?:me\s+)?(?:a|an|the)?\s*(?:full|detailed|canonical|comprehensive)?\s*(?:[a-zA-Z0-9\s,\-_]+?\s+)?report\b/i;
    const standaloneReportPattern = /\b(?:intelligence|traveler|travel|business|career|relocation|investor|history|historical|technical)\s+report\b/i;

    const matchesReport = reportPattern.test(text) || standaloneReportPattern.test(text);

    if (!matchesReport) {
      return { isReportIntent: false, confidence: 0.8 };
    }

    // Determine requested mode
    let requestedMode: ReportMode = 'STANDARD';
    if (text.includes('traveler') || text.includes('travel')) requestedMode = 'TRAVELER';
    else if (text.includes('business')) requestedMode = 'BUSINESS';
    else if (text.includes('career')) requestedMode = 'CAREER';
    else if (text.includes('relocation')) requestedMode = 'RELOCATION';
    else if (text.includes('investor')) requestedMode = 'INVESTOR';
    else if (text.includes('emergency')) requestedMode = 'EMERGENCY_OPERATIONS';

    // Extract explicit location if mentioned (e.g. "on London", "for Spain", "in Taiwan", "about Paris")
    let requestedLocation: string | undefined;

    // Check for explicit "in <Location>" at word boundary
    const inLocMatch = text.match(/\bin\s+([A-Za-z\s]+?)(?:\s+(?:for|over|with|covering|targeting)\b|\.|$)/i);
    const onLocMatch = text.match(/(?:on|about|for)\s+([A-Za-z\s]+?)(?:\s+(?:for|over|with|covering|targeting|in)\b|\.|$)/i);
    const locMatch = inLocMatch || onLocMatch;

    if (locMatch && locMatch[1]) {
      const candidate = locMatch[1].trim();
      const nonLocations = ['this', 'the', 'my', 'a', 'an', 'active', 'current'];
      if (!nonLocations.includes(candidate.toLowerCase())) {
        requestedLocation = candidate.charAt(0).toUpperCase() + candidate.slice(1);
      }
    }

    return {
      isReportIntent: true,
      requestedMode,
      requestedLocation,
      confidence: 0.95
    };
  }

  /**
   * Alias for classifyIntent to maintain compatibility across callers.
   */
  public classify(utterance: string): ReportIntentClassification {
    return this.classifyIntent(utterance);
  }

  /**
   * Binds the result of a successful FlyTo into an authoritative SpatialContext for reports.
   * If FlyTo failed or is invalid, preserves the last valid context.
   */
  public bindFlyToContext(flyToResult: any, existingContext?: SpatialContext | null): SpatialContext {
    if (
      !flyToResult ||
      !flyToResult.name ||
      !Array.isArray(flyToResult.coordinates) ||
      flyToResult.coordinates.length < 2 ||
      flyToResult.coordinates[0] === null ||
      flyToResult.coordinates[1] === null
    ) {
      if (existingContext) return existingContext;
      throw new Error('bindFlyToContext: Invalid FlyTo result and no existing context to fallback to.');
    }

    const [lon, lat] = flyToResult.coordinates;
    const scope = flyToResult.scope || this.resolveScope(flyToResult.name, flyToResult.country);

    return {
      name: flyToResult.name,
      latitude: lat,
      longitude: lon,
      country: flyToResult.country || undefined,
      scope,
      observedAt: new Date().toISOString()
    };
  }

  /**
   * Resolves the target location for a report request.
   * Explicit location in the utterance outranks inherited context.
   * If neither exists, returns clarification state without falling back to Miami.
   */
  public resolveReportTarget(utterance: string, activeContext: SpatialContext | null): ReportTargetResolution {
    const classification = this.classifyIntent(utterance);

    // Explicit override in the utterance
    if (classification.requestedLocation) {
      return {
        resolvedLocation: classification.requestedLocation,
        spatialContext: null, // Needs geocoding/acquisition for override
        isOverridden: true,
        needsClarification: false
      };
    }

    // Contextual resolution from active spatial context
    if (activeContext && activeContext.name) {
      return {
        resolvedLocation: activeContext.name,
        spatialContext: activeContext,
        isOverridden: false,
        needsClarification: false
      };
    }

    // Missing context: do NOT default to Miami!
    return {
      resolvedLocation: null,
      spatialContext: null,
      isOverridden: false,
      needsClarification: true,
      clarificationPrompt: "Which location would you like an intelligence report for?"
    };
  }

  /**
   * Creates a session coordinator to guarantee:
   * 1. One user request = One canonical report generation (no duplicate dispatches).
   * 2. Late responses cannot overwrite newer requests (request cancellation/supersession).
   */
  public createSessionCoordinator(): SessionCoordinator {
    let latestRequestId: string | null = null;
    const dispatchedSet = new Set<string>();

    return {
      createRequest: (location: string) => {
        const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
        latestRequestId = requestId;
        return { requestId, location };
      },
      isLatest: (requestId: string) => {
        return latestRequestId === requestId;
      },
      shouldApplyResult: (requestId: string) => {
        return latestRequestId === requestId;
      },
      tryDispatch: (dispatchId: string) => {
        if (dispatchedSet.has(dispatchId)) {
          return false; // Already dispatched, prevent duplicate
        }
        dispatchedSet.add(dispatchId);
        return true;
      },
      releaseDispatch: (dispatchId: string) => {
        dispatchedSet.delete(dispatchId);
      }
    };
  }

  /**
   * Resolves appropriate spatial scope without forcing city/node semantics onto countries.
   * Accepts optional countryCode (ISO 3166-1 alpha-2) for structured geocoder signals.
   *
   * Resolution priority:
   *   1. locationName itself is a known country name → NATIONAL
   *   2. locationName matches the country field (identity) → NATIONAL
   *   3. countryCode recognized AND locationName matches country → NATIONAL
   *   4. Otherwise → METRO (city/local scope)
   */
  public resolveScope(locationName: string, country?: string, countryCode?: string): SpatialScope {
    const norm = (locationName || '').trim().toLowerCase();
    const normCountry = (country || '').trim().toLowerCase();
    const normCode = (countryCode || '').trim().toUpperCase();

    // 1. The location name itself is a recognized sovereign nation
    if (ReportIntentClassifier.KNOWN_COUNTRIES.has(norm)) {
      return 'NATIONAL';
    }

    // 2. The location name matches the country field (identity: e.g. Jamaica/Jamaica)
    if (normCountry && norm === normCountry) {
      return 'NATIONAL';
    }

    // 3. Structured geocoder signal: recognized ISO code + location matches country
    if (normCode && ReportIntentClassifier.KNOWN_COUNTRY_CODES.has(normCode)) {
      if (normCountry && norm === normCountry) {
        return 'NATIONAL';
      }
    }

    return 'METRO';
  }

  /**
   * Enforces cost control: panning, zooming, and camera animation frames
   * must NEVER trigger report pipeline execution.
   */
  public shouldTriggerReport(action: string): boolean {
    const act = (action || '').trim().toLowerCase();
    const prohibitedActions = [
      'pan', 'zoom', 'drag', 'rotate', 'pitch', 'flyto_animation_frame',
      'camera_move', 'tab_change', 'panel_open', 'user_dragging'
    ];
    return !prohibitedActions.includes(act);
  }
}
